/** The form state of a job description, and the payload the API expects. */

import { sectorsInfoList } from 'helper/helper';
import { templateText } from './templateTexts';

const SECTORS_INFO = sectorsInfoList();

// A template holds the readable sector name, the same one a mission gets from
// its qualification type.
export const activityFromQualificationType = (type) =>
  SECTORS_INFO[type]?.name || type || '';

export const TOOL_PROVIDERS = ['company', 'temp_worker'];
export const DEFAULT_TOOL_PROVIDER = 'temp_worker';

export const TOOL_PROVIDER_LABELS = {
  company: 'Entreprise',
  temp_worker: 'Intérimaire',
};

export const EMPTY_TEMPLATE_FORM = {
  name: '',
  activity: '',
  // Only filters the qualification list; the template stores `activity`.
  sector: '',
  qualification: '',
  qualificationLabel: '',
  tools: [],
  licences: [],
  languages: [],
  contact_name: '',
  contact_poste: 'Responsable',
  location: '',
  salary: '',
  isIndexedToMinimumWage: false,
  position_at_risk: false,
  penibilite: {},
  dressCode: '',
  // Tasks of the chosen qualification, non archived, in their order.
  taskProposals: [],
  // Qualification tasks stored on the edited template: they stay listed, and
  // checked, even once the qualification no longer offers them.
  templateQualificationTasks: [],
  // What the admin ticked or unticked, by task key; any other proposal falls
  // back on taskProposalsCheckedByDefault.
  taskChecks: {},
  taskProposalsCheckedByDefault: true,
  // Labels the admin added, newest first.
  companyTasks: [],
  // Added tasks the AI is still checking, newest first: { key, label,
  // insertAt }. Never sent to the API.
  pendingTasks: [],
  // Why the last added task is not in the list: null, { type: 'duplicate' }
  // or { type: 'rejected', label }.
  taskNotice: null,
};

export const DRESS_CODE_MAX_LENGTH = 500;
export const TASK_LABEL_MAX_LENGTH = 80;

// Some names of the tools referential start with a space.
const toolNameKey = (name) =>
  typeof name === 'string' ? name.trim().toLowerCase() : '';

export const normalizeToolProvider = (provider) =>
  TOOL_PROVIDERS.includes(provider) ? provider : DEFAULT_TOOL_PROVIDER;

/** Legacy templates still hold plain strings instead of {name, providedBy}. */
export const normalizeTemplateTools = (input) => {
  if (!Array.isArray(input)) return [];
  const seen = new Set();
  return input.reduce((tools, item) => {
    const rawName = typeof item === 'string' ? item : item?.name;
    const key = toolNameKey(rawName);
    if (!key || seen.has(key)) return tools;
    seen.add(key);
    tools.push({
      name: rawName.trim(),
      providedBy: normalizeToolProvider(item?.providedBy),
    });
    return tools;
  }, []);
};

// The API sends the ten BeSTT keys on read and reads a missing key as false,
// so the form only keeps the factors that are on.
export const normalizePenibilite = (penibilite) => {
  if (!penibilite || typeof penibilite !== 'object') return {};
  return Object.keys(penibilite).reduce((selection, besttField) => {
    if (penibilite[besttField] === true) selection[besttField] = true;
    return selection;
  }, {});
};

export const hasSelectedPenibilite = (penibilite) =>
  Object.keys(normalizePenibilite(penibilite)).length > 0;

/** Auto prefix of the job description name, as the company front writes it. */
export const templateNamePrefix = (qualificationLabel) =>
  qualificationLabel?.trim() ? `${qualificationLabel.trim()} - ` : '';

/** The typed part of the name, behind the prefix the qualification imposes. */
export const getTemplateNameSuffix = (name, qualificationLabel) => {
  const prefix = templateNamePrefix(qualificationLabel);
  const full = String(name ?? '');
  return prefix && full.startsWith(prefix) ? full.slice(prefix.length) : full;
};

// Only the part the admin typed counts: a name left at the bare prefix is empty.
export const isTemplateNameFilled = (form) => {
  const prefix = templateNamePrefix(form?.qualificationLabel);
  const name = String(form?.name ?? '');
  if (!name.trim()) return false;
  return name.startsWith(prefix) ? name.slice(prefix.length).trim() !== '' : true;
};

/**
 * Comparison key of a job description name: case and spacing are ignored, so
 * « Boucher H/F - Lyon » and « boucher h/f -  lyon » are the same name.
 * Kept aligned with normalizeTemplateName on the api side.
 */
export const normalizeTemplateName = (name) =>
  String(name ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

/** True when another active job description of the company already has that name. */
export const isTemplateNameTaken = (name, templates, currentId) => {
  const wanted = normalizeTemplateName(name);
  if (!wanted) return false;
  return (templates || []).some(
    (template) =>
      !template?.archived &&
      String(template?._id) !== String(currentId) &&
      normalizeTemplateName(template?.name) === wanted
  );
};

/**
 * Name proposed for a copy: "… (copie)", then "(copie 2)", "(copie 3)"… up to the
 * first free one, so the Dupliquer button works straight away. The qualification
 * prefix stays untouched: the suffix goes at the end.
 */
export const buildDuplicateTemplateName = (name, templates) => {
  const base = String(name ?? '').trim();
  if (!base) return '';
  const candidate = (rank) =>
    rank === 1 ? `${base} (copie)` : `${base} (copie ${rank})`;
  let rank = 1;
  while (isTemplateNameTaken(candidate(rank), templates)) {
    rank += 1;
  }
  return candidate(rank);
};

/** A task label as stored: surrounding spaces dropped, inner ones collapsed. */
export const cleanTaskLabel = (label) =>
  String(label ?? '')
    .trim()
    .replace(/\s+/g, ' ');

/**
 * Comparison key of a task label, never stored: spacing, case and accents are
 * ignored, so « Mise en rayon » and « mise  en rayón » are the same task.
 * Kept identical to normalizeTaskLabel in api (missionTemplateHelpers.js) and
 * company (utils/templateTasks.js): the three move together.
 */
export const normalizeTaskLabel = (label) =>
  String(label ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/** What a qualification suggests: its non archived tasks, in their order. */
export const getQualificationTaskProposals = (qualification) =>
  (Array.isArray(qualification?.tasks) ? qualification.tasks : [])
    .filter((task) => task && !task.archived && cleanTaskLabel(task.label))
    .slice()
    .sort(
      (a, b) =>
        (a.order ?? Number.MAX_SAFE_INTEGER) -
        (b.order ?? Number.MAX_SAFE_INTEGER)
    )
    .map((task) => ({
      qualificationTaskId: task._id ? String(task._id) : '',
      label: cleanTaskLabel(task.label),
    }));

const taskKey = (task) =>
  task.qualificationTaskId
    ? `id:${task.qualificationTaskId}`
    : `label:${normalizeTaskLabel(task.label)}`;

/**
 * The qualification tasks the step lists: the proposals first, then the
 * template's own tasks the qualification no longer offers. A proposal keeps
 * the label the template stored for it.
 */
export const getQualificationTaskRows = (form) => {
  const stored = form?.templateQualificationTasks || [];
  const storedByKey = new Map(stored.map((task) => [taskKey(task), task]));
  const checks = form?.taskChecks || {};
  const proposals = form?.taskProposals || [];
  const proposalKeys = new Set(proposals.map(taskKey));
  const rows = proposals.map((proposal) => {
    const key = taskKey(proposal);
    const storedTask = storedByKey.get(key);
    return {
      key,
      qualificationTaskId: proposal.qualificationTaskId,
      label: storedTask?.label || proposal.label,
      checked:
        key in checks
          ? checks[key]
          : !!form?.taskProposalsCheckedByDefault || !!storedTask,
      kept: false,
    };
  });
  stored
    .filter((task) => !proposalKeys.has(taskKey(task)))
    .forEach((task) => {
      const key = taskKey(task);
      rows.push({
        key,
        qualificationTaskId: task.qualificationTaskId,
        label: task.label,
        checked: key in checks ? checks[key] : true,
        kept: true,
      });
    });
  return rows;
};

/** N tasks kept out of M listed, added ones included, pending ones too. */
export const countTemplateTasks = (form) => {
  const rows = getQualificationTaskRows(form);
  const added =
    (form?.companyTasks || []).length + (form?.pendingTasks || []).length;
  return {
    selected: rows.filter((row) => row.checked).length + added,
    total: rows.length + added,
  };
};

/** True when the label is already listed: proposed, added or being checked. */
export const isTaskLabelListed = (label, form) => {
  const wanted = normalizeTaskLabel(label);
  if (!wanted) return false;
  return [
    ...getQualificationTaskRows(form).map((row) => row.label),
    ...(form?.companyTasks || []),
    ...(form?.pendingTasks || []).map((task) => task.label),
  ].some((listed) => normalizeTaskLabel(listed) === wanted);
};

// Past this delay a check counts as unavailable: the typed text is kept.
export const TASK_CHECK_TIMEOUT_MS = 15000;

let pendingTaskSequence = 0;

/** A key that tells two pending tasks apart, whatever their label. */
export const createPendingTaskKey = () => {
  pendingTaskSequence += 1;
  return `pending:${Date.now()}:${pendingTaskSequence}`;
};

export const hasPendingTasks = (form) =>
  (form?.pendingTasks || []).length > 0;

/**
 * Lists a task the AI has yet to check, on top of the added ones. `insertAt`
 * is the index it will take in companyTasks: the tasks settled after it are
 * added above it, so a late answer leaves it where it was shown.
 */
export const addPendingTask = (form, label, key) => ({
  ...form,
  pendingTasks: [
    { key, label: cleanTaskLabel(label), insertAt: 0 },
    ...(form?.pendingTasks || []),
  ],
  taskNotice: null,
});

export const removePendingTask = (form, key) => ({
  ...form,
  pendingTasks: (form?.pendingTasks || []).filter((task) => task.key !== key),
});

/** Removes an added task; the pending tasks shown below it move up with it. */
export const removeCompanyTask = (form, label) => {
  const companyTasks = form?.companyTasks || [];
  const index = companyTasks.indexOf(label);
  if (index === -1) return form;
  return {
    ...form,
    companyTasks: companyTasks.filter((_, position) => position !== index),
    pendingTasks: (form?.pendingTasks || []).map((task) =>
      task.insertAt > index ? { ...task, insertAt: task.insertAt - 1 } : task
    ),
  };
};

/**
 * What the check route answered, as the form reads it. Anything else than a
 * clear verdict (network error, timeout, unknown shape) is `unavailable`.
 */
export const readTaskCheckAnswer = (answer) => {
  if (answer?.status === 'rejected') return { status: 'rejected' };
  if (answer?.status === 'accepted') {
    const label = cleanTaskLabel(answer.label);
    if (label && label.length <= TASK_LABEL_MAX_LENGTH)
      return { status: 'accepted', label };
  }
  return { status: 'unavailable' };
};

/**
 * Settles the pending task `key` with the check result: accepted takes the
 * reformulation, unavailable keeps the typed text, rejected drops the task.
 * A task removed meanwhile leaves the form untouched.
 */
export const applyTaskCheckResult = (form, key, result) => {
  const pendingTasks = form?.pendingTasks || [];
  const position = pendingTasks.findIndex((task) => task.key === key);
  if (position === -1) return form;
  const pending = pendingTasks[position];
  const others = pendingTasks.filter((task) => task.key !== key);
  if (result?.status === 'rejected') {
    return {
      ...form,
      pendingTasks: others,
      taskNotice: { type: 'rejected', label: pending.label },
    };
  }
  const label =
    result?.status === 'accepted' && cleanTaskLabel(result.label)
      ? cleanTaskLabel(result.label)
      : pending.label;
  // Other pending tasks are not compared: one of them may still be rejected.
  if (isTaskLabelListed(label, { ...form, pendingTasks: [] })) {
    return {
      ...form,
      pendingTasks: others,
      taskNotice: { type: 'duplicate' },
    };
  }
  const companyTasks = [...(form?.companyTasks || [])];
  const insertAt = Math.min(Math.max(pending.insertAt, 0), companyTasks.length);
  companyTasks.splice(insertAt, 0, label);
  return {
    ...form,
    companyTasks,
    // The older pending tasks are shown below this one: their slot moves down.
    pendingTasks: others.map((task, index) =>
      index >= position ? { ...task, insertAt: task.insertAt + 1 } : task
    ),
  };
};

/** The added tasks in display order, the pending ones where they were added. */
export const getAddedTaskItems = (form) => {
  const companyTasks = form?.companyTasks || [];
  const pendingTasks = form?.pendingTasks || [];
  const items = [];
  const pushPendingAt = (index) =>
    pendingTasks
      .filter((task) =>
        index < companyTasks.length
          ? task.insertAt === index
          : task.insertAt >= index
      )
      .forEach((task) =>
        items.push({ key: task.key, label: task.label, pending: true })
      );
  companyTasks.forEach((label, index) => {
    pushPendingAt(index);
    items.push({ key: `company:${label}`, label, pending: false });
  });
  pushPendingAt(companyTasks.length);
  return items;
};

/**
 * The tasks the API stores, in display order: the added ones as shown, then
 * the checked qualification tasks. A duplicate keeps its first occurrence.
 * A task still being checked is never sent.
 */
export const buildTemplateTasks = (form) => {
  const tasks = [
    ...(form?.companyTasks || []).map((label) => ({
      label: cleanTaskLabel(label),
      source: 'company',
    })),
    ...getQualificationTaskRows(form)
      .filter((row) => row.checked)
      .map((row) => ({
        ...(row.qualificationTaskId
          ? { qualificationTaskId: row.qualificationTaskId }
          : {}),
        label: cleanTaskLabel(row.label),
        source: 'qualification',
      })),
  ];
  const seen = new Set();
  return tasks.filter((task) => {
    const key = normalizeTaskLabel(task.label);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// A template saved before tasks existed has none: its step starts as a
// creation would, every proposal checked.
const tasksToFormData = (input) => {
  const tasks = (Array.isArray(input) ? input : []).filter(
    (task) => task && cleanTaskLabel(task.label)
  );
  return {
    taskProposals: [],
    templateQualificationTasks: tasks
      .filter((task) => task.source !== 'company')
      .map((task) => ({
        qualificationTaskId: task.qualificationTaskId
          ? String(task.qualificationTaskId)
          : '',
        label: cleanTaskLabel(task.label),
      })),
    taskChecks: {},
    taskProposalsCheckedByDefault: tasks.length === 0,
    companyTasks: tasks
      .filter((task) => task.source === 'company')
      .map((task) => cleanTaskLabel(task.label)),
  };
};

/** Another qualification brings its own proposals, all checked; added tasks stay. */
export const withQualificationTasks = (form, qualification) => ({
  ...form,
  taskProposals: getQualificationTaskProposals(qualification),
  templateQualificationTasks: [],
  taskChecks: {},
  taskProposalsCheckedByDefault: true,
});

export const templateToFormData = (template) => ({
  ...EMPTY_TEMPLATE_FORM,
  name: template?.name || '',
  activity: template?.activity || '',
  // The id is resolved from the label once the referential is loaded.
  qualification: '',
  qualificationLabel: template?.qualification || template?.title || '',
  tools: normalizeTemplateTools(template?.tools),
  licences: template?.licences || [],
  languages: template?.language || [],
  contact_name: template?.contact?.name || '',
  contact_poste: template?.contact?.poste || '',
  location: template?.location || '',
  salary:
    typeof template?.salary?.wage_per_hour === 'number'
      ? String(template.salary.wage_per_hour)
      : '',
  isIndexedToMinimumWage: !!template?.isIndexedToMinimumWage,
  position_at_risk: !!template?.conditions?.position_at_risk,
  penibilite: normalizePenibilite(template?.conditions?.penibilite),
  dressCode: typeof template?.dressCode === 'string' ? template.dressCode : '',
  ...tasksToFormData(template?.tasks),
});

// The wizard keeps the selection while the switch is off so it survives a
// change of mind; the API must only receive it when the flag is on.
// Instructions (other, other_interim) belong to each mission now: the template
// no longer sends them, so the API leaves the stored ones untouched.
export const buildTemplatePayload = (form) => ({
  name: form.name?.trim(),
  title: form.qualificationLabel,
  activity: form.activity,
  qualification: form.qualificationLabel,
  tools: normalizeTemplateTools(form.tools),
  licences: form.licences,
  language: form.languages,
  contact: { name: form.contact_name, poste: form.contact_poste },
  location: form.location,
  salary: { wage_per_hour: form.salary },
  isIndexedToMinimumWage: !!form.isIndexedToMinimumWage,
  conditions: {
    position_at_risk: !!form.position_at_risk,
    penibilite: form.position_at_risk ? normalizePenibilite(form.penibilite) : {},
  },
  dressCode: String(form.dressCode ?? '').trim(),
  tasks: buildTemplateTasks(form),
});

// The same steps as the company wizard, in the same order.
export const TEMPLATE_STEPS = [
  'activity',
  'risk',
  'prerequis',
  'tasks',
  'locationSalary',
  'confirmation',
].map((key) => ({ key, label: templateText(`steps.${key}`) }));

export const TEMPLATE_STEP_KEYS = TEMPLATE_STEPS.map(({ key }) => key);

// The api refuses a duplicate with "Template name already used": both sides
// say the same thing to the admin.
export const TEMPLATE_NAME_TAKEN_MESSAGE =
  'Une fiche de poste porte déjà ce nom';
export const TEMPLATE_NAME_TAKEN_API_MESSAGE = 'Template name already used';

const hasAddress = (location) =>
  !!location &&
  (typeof location !== 'object' || Object.keys(location).length > 0);

/** What keeps a step from being left, if anything. */
export const findTemplateStepError = (stepKey, form, siblings, currentId) => {
  switch (stepKey) {
    case 'activity':
      if (!form.activity || !form.qualificationLabel)
        return 'Veuillez sélectionner un secteur et une qualification';
      if (!isTemplateNameFilled(form))
        return "Veuillez indiquer l'intitulé de la fiche de poste";
      if (isTemplateNameTaken(form.name, siblings, currentId))
        return TEMPLATE_NAME_TAKEN_MESSAGE;
      return null;
    case 'risk':
      if (form.position_at_risk && !hasSelectedPenibilite(form.penibilite))
        return 'Veuillez sélectionner au moins un facteur de pénibilité';
      return null;
    case 'prerequis':
      if (String(form.dressCode ?? '').trim().length > DRESS_CODE_MAX_LENGTH)
        return templateText('dressCode.tooLong');
      return null;
    case 'tasks':
      if (countTemplateTasks(form).selected === 0)
        return templateText('tasks.required');
      return null;
    case 'locationSalary':
      if (!hasAddress(form.location)) return 'Veuillez renseigner une adresse';
      if (!form.salary) return 'Veuillez renseigner un taux horaire';
      if (!form.contact_name?.trim() || !form.contact_poste?.trim())
        return 'Veuillez compléter les informations du responsable';
      return null;
    default:
      return null;
  }
};

/** The first step that is still incomplete, checked before saving. */
export const findTemplateFormError = (form, siblings, currentId) => {
  for (const { key } of TEMPLATE_STEPS) {
    const message = findTemplateStepError(key, form, siblings, currentId);
    if (message) return { stepKey: key, message };
  }
  return null;
};
